import {
  useCallback,
  useEffect,
  useRef,
  type CSSProperties,
  type JSX,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';

// #785 — overlay vertical scrollbar mirroring React Flow's
// ``viewport.y``. Pure presentational shape: the caller pipes the
// current ``viewport.y`` in via ``viewportY`` and pipes scroll intents
// back out via ``onScrollTo``. MatchGraph owns the React Flow
// hook binding (``useReactFlow`` / ``useViewport``) so this file
// can be unit-tested without the provider.
//
// Coordinate convention mirrors React Flow: panning DOWN makes y
// NEGATIVE, so ``viewportY = -500`` means the operator has scrolled
// 500 px down the canvas. ``onScrollTo(y)`` receives the new value
// in the same convention.

const TRACK_WIDTH_PX = 12;
const MIN_THUMB_PX = 24;
const KEY_LINE_PX = 80; // ArrowUp / ArrowDown delta — one row-ish

export interface VerticalScrollbarProps {
  /** Total content height in canvas units (largest y the layout reached). */
  contentHeight: number;
  /** Visible viewport height in **screen** px (container.clientHeight). */
  viewportHeight: number;
  /**
   * Current React Flow ``viewport.y`` in **screen** px (negative when
   * scrolled down). React Flow's viewport already encodes the screen-space
   * transform — we don't need to scale this prop.
   */
  viewportY: number;
  /**
   * Current React Flow zoom factor. Required to convert ``contentHeight``
   * (canvas units) to screen units so the thumb maps correctly when the
   * operator zooms via the built-in Controls. Defaults to 1 — callers on
   * a non-zooming canvas can omit.
   */
  zoom?: number;
  /** Called with the new ``viewport.y`` in screen px. */
  onScrollTo: (y: number) => void;
  /** Track height in screen px. Defaults to ``viewportHeight``. */
  trackHeight?: number;
}

function clamp(value: number, lo: number, hi: number): number {
  if (hi < lo) return lo;
  return Math.max(lo, Math.min(hi, value));
}

export function VerticalScrollbar({
  contentHeight,
  viewportHeight,
  viewportY,
  zoom = 1,
  onScrollTo,
  trackHeight,
}: VerticalScrollbarProps): JSX.Element | null {
  // Codex round 1 P2 (#785) — ``viewport.y`` and ``viewportHeight`` are
  // in SCREEN units (post-transform), but ``contentHeight`` came in as
  // canvas units. Scale by zoom so both sides of the maxScroll equation
  // share units; otherwise zooming via the React Flow Controls breaks
  // the thumb mapping (clamps too early at zoom > 1, overscrolls at
  // zoom < 1).
  const scaledContentHeight = contentHeight * zoom;
  const maxScroll = Math.max(0, scaledContentHeight - viewportHeight);
  const trackPx = trackHeight ?? viewportHeight;
  const trackRef = useRef<HTMLDivElement | null>(null);
  // Drag-state lives in a ref so mousemove listeners can read it
  // without forcing rerenders for every mouse motion (would jank).
  const dragStateRef = useRef<{
    startClientY: number;
    startViewportY: number;
  } | null>(null);

  // Pure clamp helper — used by every event handler so the caller
  // never sees a viewportY outside ``[-maxScroll, 0]``.
  const clampScroll = useCallback(
    (next: number) => clamp(next, -maxScroll, 0),
    [maxScroll],
  );

  // Thumb geometry — derived from current viewportY each render. No
  // local state: viewportY IS the source of truth. Use scaled height
  // so the thumb shrinks when zoomed in (less content visible per pan).
  const thumbHeight = (() => {
    if (scaledContentHeight <= 0) return MIN_THUMB_PX;
    const ratio = viewportHeight / scaledContentHeight;
    return Math.max(MIN_THUMB_PX, Math.round(ratio * trackPx));
  })();
  const thumbTravel = Math.max(0, trackPx - thumbHeight);
  // Codex round 2 P2 (#785) — React Flow lets the operator pan the
  // pane directly (drag-on-canvas), and that pan is NOT clamped to
  // ``[-maxScroll, 0]``. Without this clamp the thumb would render
  // off the track top/bottom when the canvas is dragged past the
  // extent (and ``aria-valuenow`` would leave its declared range).
  // We clamp only the values used for geometry + ARIA so the
  // underlying pan stays free — the operator can still slide past
  // the end; the thumb just pins to the extent that direction.
  const displayY = clampScroll(viewportY);
  const thumbTop = maxScroll > 0
    ? Math.round((-displayY / maxScroll) * thumbTravel)
    : 0;

  // Drag handlers attached to ``window`` while dragging so the operator
  // can pull the thumb past the track edges without losing the drag.
  useEffect(() => {
    function onMove(ev: MouseEvent) {
      const drag = dragStateRef.current;
      if (!drag) return;
      if (thumbTravel <= 0) return;
      const deltaScreen = ev.clientY - drag.startClientY;
      // Translate screen-space drag into canvas-space scroll. Multiply
      // by ``maxScroll / thumbTravel`` because each thumb pixel maps to
      // (content / track) canvas pixels.
      const deltaCanvas = (deltaScreen * maxScroll) / thumbTravel;
      onScrollTo(clampScroll(drag.startViewportY - deltaCanvas));
    }
    function onUp() {
      if (!dragStateRef.current) return;
      dragStateRef.current = null;
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [clampScroll, maxScroll, onScrollTo, thumbTravel]);

  const handleTrackMouseDown = useCallback(
    (ev: ReactMouseEvent<HTMLDivElement>) => {
      if (ev.button !== 0) return;
      const track = trackRef.current;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      const pointerY = ev.clientY - rect.top;
      // Was the click on the thumb itself? If so, start a drag.
      // Codex round-3 P2 on PR #798 (#804): base interaction maths on
      // the CLAMPED ``displayY`` rather than raw ``viewportY`` so that
      // the very first interaction after an over-pan (React Flow
      // pane-drag past ``[-maxScroll, 0]``) resolves in one step
      // instead of feeling stuck at the extent.
      if (pointerY >= thumbTop && pointerY <= thumbTop + thumbHeight) {
        dragStateRef.current = {
          startClientY: ev.clientY,
          startViewportY: displayY,
        };
        return;
      }
      // Click on track above or below the thumb → page by viewportHeight.
      const direction = pointerY < thumbTop ? +1 : -1; // up → positive y (less negative)
      onScrollTo(clampScroll(displayY + direction * viewportHeight));
    },
    [clampScroll, displayY, onScrollTo, thumbHeight, thumbTop, viewportHeight],
  );

  const handleKeyDown = useCallback(
    (ev: KeyboardEvent<HTMLDivElement>) => {
      let next: number | null = null;
      switch (ev.key) {
        case 'ArrowDown':
          next = displayY - KEY_LINE_PX;
          break;
        case 'ArrowUp':
          next = displayY + KEY_LINE_PX;
          break;
        case 'PageDown':
          next = displayY - viewportHeight;
          break;
        case 'PageUp':
          next = displayY + viewportHeight;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = -maxScroll;
          break;
        default:
          return;
      }
      ev.preventDefault();
      onScrollTo(clampScroll(next));
    },
    [clampScroll, displayY, maxScroll, onScrollTo, viewportHeight],
  );

  if (maxScroll <= 0) return null;

  const trackStyle: CSSProperties = {
    position: 'absolute',
    top: 0,
    right: 0,
    width: TRACK_WIDTH_PX,
    height: trackPx,
    background: 'rgba(15, 23, 42, 0.05)',
    borderRadius: 6,
    touchAction: 'none',
    outline: 'none',
    zIndex: 5,
  };
  const thumbStyle: CSSProperties = {
    position: 'absolute',
    top: thumbTop,
    left: 1,
    right: 1,
    height: thumbHeight,
    background: 'rgba(15, 23, 42, 0.45)',
    borderRadius: 5,
    pointerEvents: 'none',
  };

  return (
    <div
      ref={trackRef}
      data-testid="gutschrift-vertical-scrollbar"
      role="scrollbar"
      aria-orientation="vertical"
      aria-valuemin={0}
      aria-valuemax={maxScroll}
      aria-valuenow={Math.round(-displayY)}
      tabIndex={0}
      onMouseDown={handleTrackMouseDown}
      onKeyDown={handleKeyDown}
      style={trackStyle}
    >
      <div data-testid="gutschrift-vertical-scrollbar-thumb" style={thumbStyle} />
    </div>
  );
}
